import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'
import { Tooltip } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { createColumnHelper, useTable } from '@tanstack/react-table'
import dayjs from 'dayjs'

import Api from '@vacationtracker/shared/services/api'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { userHasDefaultWorkingHours } from '@vacationtracker/shared/functions/work-week'
import { spacing } from '@vacationtracker/shared/constants/ThemeSetting'

import { Button } from '../../design-system/Button'
import { CheckboxField } from '../../design-system/CheckboxField'
import { ConfirmModal } from '../../design-system/ConfirmModal'
import { DataGrid, dataGridFeatures } from '../../design-system/DataGrid'
import type { DataGridFeatures } from '../../design-system/DataGrid'
import { DateNavigator, windowFor } from '../../design-system/DateNavigator'
import type { DateNavigatorWindow } from '../../design-system/DateNavigator'
import { Stack } from '../../design-system/Stack'
import { Text } from '../../design-system/Text'
import IntlMessages from '../../util/IntlMessages'
import { IGetLocationGeneral } from '../../types/locations'
import type { EventOutcome } from '../../util/notifications'
import { EditableCell, GridEditingProvider } from '../inline-edit/grid-cell'
import type { IEditableCellProps } from '../inline-edit/grid-cell'
import { TextEditor } from '../inline-edit/editors'
import type { SaveError } from '../inline-edit/use-save-status'
import { HolidayDatesEditor, datesPanelStyles } from './HolidayDatesEditor'
import {
  HolidayField,
  HolidayPatch,
  HolidayRow,
  HolidayRowsByYear,
  HolidayUpdatePayload,
  cellAccess,
  deleteWarnsAboutSeries,
  holidayUpdatePayload,
  holidaysForYear,
  importAllowed,
  overlappingHoliday,
  rowKey,
  rowTag,
  rowYear,
  sameIdYears,
  seriesHasPastYears,
  utcToday,
  yearRange,
} from './logic'
import { libraryRulesOf } from './library-rule'
import { HolidayDates, RecurrenceCell, SelectionBar, TypeTag, WithReason, recurrencePopoverStyles } from './parts'
import { recurrenceChangePayload, recurrenceClash } from './recurrence'
import { createCommitQueue } from './commit-queue'
import type { CommitQueue } from './commit-queue'
import { CellEdit, PENDING_EDIT_TTL_MS, PendingEdit, applyEdits, editFields, editsOf, prunePending } from './pending-edits'

// Each task already waits for its event to be reported done; this covers the projection that
// writes the year after it, which the next handler reads.
const COMMIT_GAP_MS = 500

const COLUMN_WIDTHS: Record<string, number> = {
  select: 48,
  name: 170,
  dates: 268,
  source: 110,
  recurring: 134,
  actions: 56,
}

const ROOT_CLASS = 'vt-holidays-tab'

const cellStyles = `.${ROOT_CLASS} .ant-tag { font-size: 14px; line-height: 22px; }`

const columnHelper = createColumnHelper<DataGridFeatures, HolidayRow>()

/** An accepted event, and its outcome once the backend reports it. */
export interface PostedEvent {
  settled: Promise<EventOutcome>
}

export interface LocationHolidaysProps {
  locationId: string
  locationName: string
  /** False while another tab of the page is shown: antd keeps this one mounted, and the selection bar floats over the page. */
  active?: boolean
  /** Grouped by groupMultiDayHolidays, one row per multi-day span. */
  holidays: HolidayRowsByYear[]
  workHours: IGetLocationGeneral['workHours']
  /** The location's working weekdays, 0 = Sunday, for where a transferred holiday lands. */
  workWeek?: number[]
  firstDayOfWeek: number
  locale: LocaleEnum
  /** Where an imported holiday's library rule is looked up: the place of the last import, else the location's own. */
  countryCode?: string | null
  subdivisionCode?: string | null
  /** False for a Location Admin on a location they do not manage. */
  canEdit: boolean
  /** Tracks an inline save's event, announcing only a failure; resolves with its outcome. */
  onEventPosted: (correlationId: string) => Promise<EventOutcome>
  /** A saved edit stopped outranking the read model without the read model showing it, so the rows may be stale. */
  onPendingExpired?: () => void
  /** Resolves null when the delete was not accepted. */
  onDeleteHoliday: (holidayId: string) => Promise<PostedEvent | null>
  onDeleteHolidays: (holidayIds: string[]) => Promise<PostedEvent | null>
}

interface CellInput {
  row: HolidayRow
  field: 'name' | 'dates'
  label: string
  display: React.ReactNode
  renderEditor: IEditableCellProps<HolidayPatch>['renderEditor']
}

interface BuiltEdit {
  payload: HolidayUpdatePayload
  /** The row the body was built on: the read model plus every edit queued before this one. */
  before: HolidayRow
}

interface ConfirmDeleteProps {
  open: boolean
  title: string
  message: React.ReactNode
  enqueue: CommitQueue
  /** Resolves null when the delete was not accepted. */
  onDelete?: () => Promise<PostedEvent | null>
  onClose: () => void
}

// In the queue like every other holiday event; the dialog closes once the delete is accepted.
// A confirmed delete runs whatever the user does next, so until then the dialog cannot be dismissed.
function ConfirmDelete({ open, title, message, enqueue, onDelete, onClose }: ConfirmDeleteProps): React.ReactElement {
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(false)

  const confirm = () => {
    if (!onDelete || loading) return
    setLoading(true)
    void enqueue(async () => {
      const posted = await onDelete().finally(() => setLoading(false))
      onClose()
      await posted?.settled
    })
  }

  return (
    <ConfirmModal
      open={open}
      title={title}
      message={message}
      primaryAction={{ label: formatMessage({ id: 'app.delete' }), variant: 'primary-danger', loading, onClick: confirm }}
      secondaryAction={loading ? undefined : { label: formatMessage({ id: 'app.cancel' }), onClick: onClose }}
      onClose={() => {
        if (!loading) onClose()
      }}
    />
  )
}

const LocationHolidays = (props: LocationHolidaysProps): React.ReactElement => {
  const {
    locationId,
    locationName,
    active = true,
    holidays,
    workHours,
    workWeek,
    firstDayOfWeek,
    locale,
    countryCode,
    subdivisionCode,
    canEdit,
    onEventPosted,
    onPendingExpired,
    onDeleteHoliday,
    onDeleteHolidays,
  } = props
  const { formatMessage, formatList } = useIntl()
  const place = useMemo(() => (countryCode ? { countryCode, subdivisionCode: subdivisionCode ?? null } : null), [countryCode, subdivisionCode])
  const history = useHistory()
  const { state: returned } = useLocation<{ year?: number } | undefined>()

  // The helpers read only its year, so it stays one object, and the columns one set, until the year turns.
  const utcYear = utcToday().year()
  const today = useMemo(utcToday, [utcYear])

  // Back from the import page on the year it imported into.
  const [period, setPeriod] = useState<DateNavigatorWindow>(() => windowFor('year', dayjs().year(returned?.year ?? utcYear)))
  const [deleteTarget, setDeleteTarget] = useState<HolidayRow | null>(null)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [bulkConfirming, setBulkConfirming] = useState(false)
  // Each opening gets a fresh dialog, so nothing of an earlier delete carries into it.
  const [dialogKey, nextDialogKey] = useReducer((key: number) => key + 1, 0)
  const [enqueue] = useState(() => createCommitQueue(COMMIT_GAP_MS))

  // Edits saved inline but not yet in the read model, in the order they were made. They show at
  // once, and every queued body is built on the read model plus the edits before it, so a second
  // edit to a row carries the first one.
  const pending = useRef<PendingEdit[]>([])
  const nextSeq = useRef(0)
  const [pendingVersion, pendingChanged] = useReducer((version: number) => version + 1, 0)
  const holidaysRef = useRef(holidays)
  holidaysRef.current = holidays

  const onPendingExpiredRef = useRef(onPendingExpired)
  onPendingExpiredRef.current = onPendingExpired

  const pruneDropped = (): boolean => {
    if (pending.current.length === 0) return false
    const kept = prunePending({ pending: pending.current, rows: holidaysRef.current.flatMap((entry) => entry.holidays), now: Date.now() })
    if (kept.length === pending.current.length) return false
    pending.current = kept
    pendingChanged()
    return true
  }

  useEffect(() => {
    pruneDropped()
  }, [holidays])

  // A saved edit's deadline passes whether or not the rows change, and the read model it gives way to may be the stale one.
  useEffect(() => {
    const savedAt = pending.current.flatMap((entry) => (entry.savedAt === undefined ? [] : [entry.savedAt]))
    if (savedAt.length === 0) return undefined
    const timer = setTimeout(
      () => {
        if (pruneDropped()) onPendingExpiredRef.current?.()
      },
      Math.max(0, Math.min(...savedAt) + PENDING_EDIT_TTL_MS + 1 - Date.now())
    )
    return () => clearTimeout(timer)
  }, [pendingVersion])

  // The rows on screen, each still known by the key of the row it came from, so a cell keeps its
  // identity (and its open editor or save status) while an edit to the row is pending.
  const { shown, keyOf } = useMemo(() => {
    if (pending.current.length === 0) return { shown: holidays, keyOf: rowKey }
    const pendingKeys = new Set(pending.current.flatMap((entry) => entry.keys))
    const keys = new Map<HolidayRow, string>()
    const overlay = (row: HolidayRow): HolidayRow => {
      const key = rowKey(row)
      if (!pendingKeys.has(key)) return row
      const edited = applyEdits(row, editsOf([key], pending.current))
      keys.set(edited, key)
      return edited
    }
    const byYear = holidays.map((entry) => (entry.holidays.some((row) => pendingKeys.has(rowKey(row))) ? { ...entry, holidays: entry.holidays.map(overlay) } : entry))
    return { shown: byYear, keyOf: (row: HolidayRow) => keys.get(row) ?? rowKey(row) }
  }, [holidays, pendingVersion])

  const year = period.start.year()
  const rows = useMemo(() => holidaysForYear(shown, year), [shown, year])
  const libraryRules = useMemo(() => libraryRulesOf(rows, place), [rows, place])
  // A row without an id is legacy data the delete events cannot name.
  const selectableRows = useMemo(() => rows.filter((row) => Boolean(row.id)), [rows])
  // Read through the rows, so a row a refetch removed no longer counts as selected.
  const selectedRows = useMemo(() => (canEdit ? selectableRows.filter((row) => selected.has(keyOf(row))) : []), [canEdit, selectableRows, selected, keyOf])
  const selectedCount = selectedRows.length
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length

  const changePeriod = (next: DateNavigatorWindow) => {
    setPeriod(next)
    setSelected(new Set())
  }

  const openDelete = (row: HolidayRow) => {
    nextDialogKey()
    setDeleteTarget(row)
  }

  const toggle = (key: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })
  const range = useMemo(() => yearRange(holidays, today), [holidays, today])
  const allowHalfDay = userHasDefaultWorkingHours(workHours)

  const onEventPostedRef = useRef(onEventPosted)
  onEventPostedRef.current = onEventPosted

  const overlapError = (name: string, clashYear: number, ownYear: number) =>
    new Error(clashYear === ownYear ? formatMessage({ id: 'holidaysTab.overlap' }, { name }) : formatMessage({ id: 'holidaysTab.overlapInYear' }, { name, year: clashYear }))

  // The body for an edit to the row known by `keys`, on the read model plus the edits made before
  // `beforeSeq`; null when there is nothing to post. Throws on a clash the backend would reject.
  const buildEdit = (keys: string[], edit: CellEdit, beforeSeq: number): BuiltEdit | null => {
    const earlier = pending.current.filter((entry) => entry.seq < beforeSeq)
    // A clash is only ever looked for from the row's own year on.
    const editedFrom = (fromYear: number, toYear: number): HolidayRowsByYear[] =>
      holidaysRef.current
        .filter((entry) => Number(entry.year) >= fromYear && Number(entry.year) <= toYear)
        .map((entry) => ({ ...entry, holidays: entry.holidays.map((row) => applyEdits(row, editsOf([rowKey(row)], earlier))) }))
    const base = holidaysRef.current.flatMap((entry) => entry.holidays).find((row) => keys.includes(rowKey(row)))
    if (!base) {
      // Deleted, or re-identified by a save, since the edit was made: posting it would act on what is no longer there.
      throw new Error()
    }
    const before = applyEdits(base, editsOf(keys, earlier))
    const ownYear = rowYear(before)
    if (edit.field === 'recurring') {
      const payload = recurrenceChangePayload({ row: before, locationId, settings: edit.settings })
      const clash = payload && recurrenceClash({ row: before, payload, byYear: editedFrom(ownYear, Number.POSITIVE_INFINITY), workWeek })
      if (clash) throw overlapError(clash.name, clash.year, ownYear)
      return payload && { payload, before }
    }
    const payload = holidayUpdatePayload({ row: before, locationId, patch: edit.patch })
    const clash = edit.field === 'dates' ? overlappingHoliday(before, payload, holidaysForYear(editedFrom(ownYear, ownYear), ownYear)) : undefined
    if (clash) throw overlapError(clash.name, ownYear, ownYear)
    return { payload, before }
  }

  const dropPending = (entry: PendingEdit) => {
    pending.current = pending.current.filter((other) => other !== entry)
    pendingChanged()
  }

  const followMove = (keys: string[], movedTo: string) => {
    pending.current = pending.current.map((entry) =>
      entry.keys.some((key) => keys.includes(key)) && !entry.keys.includes(movedTo) ? { ...entry, keys: [...entry.keys, movedTo] } : entry
    )
  }

  /**
   * Shows the edit at once and saves it in turn: its body is built when the queue reaches it, and
   * it settles when the backend reports the event, so "saved" means saved. Resolves with the
   * row's new cell key when the save moved it.
   */
  const commitEdit = async (row: HolidayRow, field: HolidayField, edit: CellEdit): Promise<string | void> => {
    const key = keyOf(row)
    const now = buildEdit([key], edit, Number.POSITIVE_INFINITY)
    if (!now) return
    nextSeq.current += 1
    let entry: PendingEdit = { seq: nextSeq.current, keys: [key], fields: editFields(edit, now.payload, now.before) }
    pending.current = [...pending.current, entry]
    pendingChanged()

    const replace = (next: PendingEdit) => {
      pending.current = pending.current.map((other) => (other.seq === next.seq ? next : other))
      entry = next
      pendingChanged()
    }
    const latest = () => pending.current.find((other) => other.seq === entry.seq) ?? entry

    try {
      return await enqueue(async () => {
        const current = latest()
        const built = buildEdit(current.keys, edit, current.seq)
        if (!built) {
          dropPending(current)
          return undefined
        }
        replace({ ...current, fields: editFields(edit, built.payload, built.before) })
        const response = await Api.post('/core/event', built.payload)
        const outcome = await onEventPostedRef.current(response.correlationId)
        if (outcome !== 'success') {
          // The failure itself is announced by the event's notification.
          throw new Error()
        }
        replace({ ...entry, savedAt: Date.now() })
        const movedTo = rowKey({ id: built.payload.id, date: entry.fields.date ?? built.before.date })
        // An earlier queued edit may have moved the row already, so this can differ from the cell's key without a follow.
        if (!entry.keys.includes(movedTo)) followMove(entry.keys, movedTo)
        return movedTo === key ? undefined : `${movedTo}:${field}`
      })
    } catch (error) {
      const current = latest()
      dropPending(current)
      const failure: SaveError = error instanceof Error ? error : new Error()
      const latestKey = current.keys[current.keys.length - 1]
      if (latestKey !== key) failure.movedTo = `${latestKey}:${field}`
      throw failure
    }
  }

  const columns = useMemo(() => {
    const cell = ({ row, field, label, display, renderEditor }: CellInput) => {
      const access = cellAccess({ field, row, canEdit, today })
      return (
        <EditableCell<HolidayPatch>
          cellKey={`${keyOf(row)}:${field}`}
          editable={access.editable}
          fit="content"
          lockReason={access.lockReason}
          affordance={field === 'dates' ? 'calendar' : 'pencil'}
          editLabel={formatMessage({ id: 'locationGeneral.editField' }, { field: label })}
          display={display}
          errorMessage={formatMessage({ id: 'error.generic' })}
          renderEditor={renderEditor}
          onCommit={(patch) => commitEdit(row, field, { field, patch })}
        />
      )
    }

    return columnHelper.columns([
      ...(canEdit
        ? [
            columnHelper.display({
              id: 'select',
              header: () => (
                <CheckboxField
                  ariaLabel={formatMessage({ id: 'holidaysTab.bulk.selectAll' }, { year })}
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(selectableRows.map(keyOf)))}
                />
              ),
              cell: ({ row: { original } }) =>
                original.id ? (
                  <CheckboxField
                    ariaLabel={formatMessage({ id: 'holidaysTab.bulk.selectRow' }, { name: original.name })}
                    checked={selected.has(keyOf(original))}
                    onChange={(on) => toggle(keyOf(original), on)}
                  />
                ) : null,
            }),
          ]
        : []),
      columnHelper.display({
        id: 'name',
        header: () => formatMessage({ id: 'holidays.holidaysName' }),
        cell: ({ row: { original } }) => {
          const label = formatMessage({ id: 'app.name' })
          return cell({
            row: original,
            field: 'name',
            label,
            display: original.name,
            renderEditor: ({ commit, cancel }) => {
              const editor = (
                <TextEditor
                  ariaLabel={label}
                  size="small"
                  value={original.name}
                  cancel={cancel}
                  commit={(name) => {
                    const trimmed = name.trim()
                    if (trimmed && trimmed !== original.name) {
                      commit({ name: trimmed })
                    } else {
                      cancel()
                    }
                  }}
                />
              )
              // The backend renames an imported repeating holiday in this year only; later years keep the library's name.
              if (original.type !== 'LIB' || !original.recurring) return editor
              return (
                <Stack gap="xxs">
                  {editor}
                  <Text tone="secondary">{formatMessage({ id: 'holidaysTab.recurrence.thisYearOnly' }, { year: rowYear(original) })}</Text>
                </Stack>
              )
            },
          })
        },
      }),
      columnHelper.display({
        id: 'dates',
        header: () => formatMessage({ id: 'components.leavesColumns.dates' }),
        cell: ({ row: { original } }) =>
          cell({
            row: original,
            field: 'dates',
            label: formatMessage({ id: 'components.leavesColumns.dates' }),
            display: <HolidayDates row={original} locale={locale} />,
            renderEditor: ({ commit, cancel }) => (
              <HolidayDatesEditor row={original} allowHalfDay={allowHalfDay} weekStart={firstDayOfWeek} today={today} commit={commit} cancel={cancel} />
            ),
          }),
      }),
      columnHelper.display({
        id: 'source',
        header: () => formatMessage({ id: 'app.type' }),
        cell: ({ row: { original } }) => <TypeTag tag={rowTag(original, libraryRules.get(original)?.type)} />,
      }),
      columnHelper.display({
        id: 'recurring',
        header: () => (
          <WithReason reason={formatMessage({ id: 'holidaysTab.recurrence.headerTooltip' })}>
            <span>{formatMessage({ id: 'holidaysTab.recurrence.header' })}</span>
          </WithReason>
        ),
        cell: ({ row: { original } }) => (
          <RecurrenceCell
            row={original}
            locationId={locationId}
            cellKey={`${keyOf(original)}:recurring`}
            canEdit={canEdit}
            libraryRule={libraryRules.get(original)}
            today={today}
            onCommit={(settings) => commitEdit(original, 'recurring', { field: 'recurring', settings })}
          />
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => '',
        cell: ({ row: { original } }) => {
          if (!canEdit || !original.id) return null
          const deleteLabel = formatMessage({ id: 'holidaysTab.deleteNamed' }, { name: original.name })
          return (
            <Stack direction="horizontal" gap="xxs" justify="flex-end">
              <Tooltip title={deleteLabel}>
                <Button variant="text" iconOnly icon={<DeleteOutlined />} aria-label={deleteLabel} onClick={() => openDelete(original)} />
              </Tooltip>
            </Stack>
          )
        },
      }),
    ])
    // workWeek stands in for commitEdit, which the cells call and which reads it.
  }, [formatMessage, canEdit, locale, allowHalfDay, firstDayOfWeek, locationId, workWeek, libraryRules, year, today, selectableRows, selected, allSelected, selectedCount, keyOf])

  const table = useTable({ features: dataGridFeatures, columns, data: rows, getRowId: keyOf })

  // A grouped multi-day row's id is enough: the backend removes the whole group.
  const deleteSelected = async (): Promise<PostedEvent | null> => {
    const deleted = new Set(selectedRows.map(keyOf))
    const posted = await onDeleteHolidays([...new Set(selectedRows.map((row) => row.id))])
    if (posted) {
      // Rows selected after confirming were not part of this delete.
      setSelected((current) => new Set([...current].filter((key) => !deleted.has(key))))
    }
    return posted
  }

  const importReason = importAllowed(year, today) ? undefined : formatMessage({ id: 'holidaysTab.import.pastYearOnlyLast' })

  const actions = (
    <Stack direction="horizontal" gap="sm" align="center">
      <WithReason reason={importReason}>
        <Button variant="outline" className="joyride-import-holidays" disabled={Boolean(importReason)} onClick={() => history.push(`/app/import-holidays/${locationId}/${year}`)}>
          <IntlMessages id="holidays.importHolidays" />
        </Button>
      </WithReason>
      <Button variant="outline" onClick={() => history.push(`/app/holiday/${locationId}`, { year })}>
        <IntlMessages id="components.locationHolidays.addHolidays" />
      </Button>
    </Stack>
  )

  const empty = rows.length === 0

  const reachOf = (row: HolidayRow): string[] => {
    const years = sameIdYears(row, holidays)
    return [
      ...(seriesHasPastYears(row, holidays) ? [formatMessage({ id: 'holidaysTab.deleteSeriesPast' })] : []),
      ...(years.length > 0 ? [formatMessage({ id: 'holidaysTab.deleteSharedId' }, { years: formatList(years.map(String)) })] : []),
    ]
  }

  const paragraphs = (items: { key: string; text: React.ReactNode }[]) => (
    <Stack gap="sm">
      {items.map(({ key, text }) => (
        <span key={key}>{text}</span>
      ))}
    </Stack>
  )

  const deleteMessage = (row: HolidayRow) =>
    paragraphs([
      ...(deleteWarnsAboutSeries(row) ? [{ key: 'series', text: formatMessage({ id: 'components.locationHolidays.deleteHolidaysConfirmRecurring' }) }] : []),
      ...reachOf(row).map((sentence) => ({ key: sentence, text: sentence })),
      { key: 'confirm', text: formatMessage({ id: 'components.locationHolidays.deleteHolidaysConfirm' }, { name: row.name }) },
    ])

  // The series sentence only when a selected row is part of a series; each further reach is named
  // by its row when more than one row is selected, since its sentence speaks of one holiday.
  const bulkMessage = () => {
    const series = selectedRows.some(deleteWarnsAboutSeries)
    const reaches = selectedRows.map((row) => ({ row, sentences: reachOf(row) })).filter(({ sentences }) => sentences.length > 0)
    if (!series && reaches.length === 0) return null
    return paragraphs([
      ...(series ? [{ key: 'series', text: formatMessage({ id: 'holidaysTab.bulkDelete.message' }, { location: locationName, year }) }] : []),
      ...reaches.map(({ row, sentences }) => ({
        key: keyOf(row),
        text:
          selectedRows.length === 1 ? (
            sentences.join(' ')
          ) : (
            <>
              <strong>{row.name}</strong> – {sentences.join(' ')}
            </>
          ),
      })),
    ])
  }

  return (
    <GridEditingProvider>
      <style>{datesPanelStyles + recurrencePopoverStyles + cellStyles}</style>
      <Stack gap="base" className={ROOT_CLASS}>
        <Stack direction="horizontal" justify="space-between" align="center" gap="sm" wrap>
          <DateNavigator
            value={period}
            onChange={changePeriod}
            units={['year']}
            showPicker={false}
            disabledMonth={(month) => month.year() < range.first || month.year() > range.last}
            labels={{
              previous: formatMessage({ id: 'dateNavigator.previousYear' }),
              next: formatMessage({ id: 'dateNavigator.nextYear' }),
              thisYear: formatMessage({ id: 'app.thisYear' }),
            }}
          />
          {canEdit && !empty && actions}
        </Stack>

        {!canEdit && (
          <Text tone="secondary">
            <IntlMessages id="locationGeneral.lockedNotYours" />
          </Text>
        )}

        {active && selectedCount > 0 && (
          <SelectionBar
            count={selectedCount}
            onClear={() => setSelected(new Set())}
            onDelete={() => {
              // The bar floats outside the dialog; a new key would remount an open dialog mid-delete.
              if (bulkConfirming || deleteTarget) return
              nextDialogKey()
              setBulkConfirming(true)
            }}
          />
        )}

        {empty ? (
          <Stack gap="sm" align="flex-start" style={{ padding: `${spacing.lg}px 0` }}>
            <Text>
              <IntlMessages id="locationHolidays.noHolidaysForYear" values={{ year }} />
            </Text>
            {canEdit && actions}
          </Stack>
        ) : (
          <DataGrid<HolidayRow>
            table={table}
            loading={false}
            columnWidths={COLUMN_WIDTHS}
            chromeColumnIds={['select', 'actions', 'source', 'recurring']}
            flexColumnIds={['name', 'dates', 'source', 'recurring']}
            wrapColumnIds={['name', 'dates']}
            rowActionsColumnId="actions"
            separators="uniform"
            skeletonRows={rows.length}
          />
        )}
      </Stack>

      <ConfirmDelete
        key={`single-${dialogKey}`}
        open={deleteTarget !== null}
        title={formatMessage({ id: 'components.locationHolidays.deleteHolidaysTitle' })}
        message={deleteTarget && deleteMessage(deleteTarget)}
        enqueue={enqueue}
        onDelete={deleteTarget ? () => onDeleteHoliday(deleteTarget.id) : undefined}
        onClose={() => setDeleteTarget((current) => (current === deleteTarget ? null : current))}
      />

      <ConfirmDelete
        key={`bulk-${dialogKey}`}
        open={bulkConfirming}
        title={formatMessage({ id: 'holidaysTab.bulk.confirmTitle' }, { count: selectedCount })}
        message={bulkConfirming && bulkMessage()}
        enqueue={enqueue}
        onDelete={deleteSelected}
        onClose={() => setBulkConfirming(false)}
      />
    </GridEditingProvider>
  )
}

export default LocationHolidays
